import { useEffect, useState, useCallback, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { ArrowLeft, Loader2, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  DEFAULT_INCLUDED_TYPES,
  DEFAULT_CLASSIFICATION_TYPES,
  DEFAULT_SCAN_CONFIG_SETTINGS,
  SCAN_CONFIG_PRESETS,
  type ScanConfigSettings,
  AI_MODELS,
} from "@/types/domain";

interface ConfigFormProps {
  configId?: string | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  city: string;
  sponsorName: string;
  includedTypes: string;
  classificationTypes: string;
  excludeInstitutions: boolean;
  excludePrivate: boolean;
  excludeNoSportField: boolean;
  classificationModel: string;
  websiteClassificationModel: string;
  webResearchModel: string;
  maxDepth: string;
  minCellArea: string;
  concurrency: string;
  screenshotZoom: string;
  rateLimit: string;
}

// ── Tag Input Component ──────────────────────────────────────────────────

function TagInput({
  tags,
  onChange,
  placeholder,
  showBulkEdit,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  showBulkEdit?: boolean;
}) {
  const [inputValue, setInputValue] = useState("");
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkValue, setBulkValue] = useState("");

  const addTag = useCallback((value: string) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInputValue("");
  }, [tags, onChange]);

  const removeTag = useCallback((index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  }, [tags, onChange]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  }, [inputValue, tags, addTag, removeTag]);

  if (isBulkMode) {
    return (
      <div className="space-y-2">
        <Textarea
          rows={4}
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
          placeholder="One type per line"
          className="font-mono text-sm"
        />
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              const newTags = bulkValue.split("\n").map((s) => s.trim()).filter(Boolean);
              onChange(newTags);
              setIsBulkMode(false);
            }}
          >
            Apply
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setBulkValue(tags.join("\n"));
              setIsBulkMode(false);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background p-2 min-h-[42px]">
        {tags.map((tag, index) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground"
          >
            {tag}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => removeTag(index)}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputValue.trim()) addTag(inputValue); }}
          placeholder={tags.length === 0 ? placeholder : "Add type..."}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {showBulkEdit && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground underline"
          onClick={() => {
            setBulkValue(tags.join("\n"));
            setIsBulkMode(true);
          }}
        >
          Bulk edit
        </button>
      )}
    </div>
  );
}

// ── ConfigForm ───────────────────────────────────────────────────────────

export function ConfigForm({ configId, onClose }: ConfigFormProps) {
  const utils = trpc.useUtils();

  const { data: existingConfig, isLoading: isLoadingConfig } = trpc.scanConfigs.getById.useQuery(
    { id: configId! },
    { enabled: !!configId }
  );

  const createMutation = trpc.scanConfigs.create.useMutation({
    onSuccess: () => {
      utils.scanConfigs.list.invalidate();
      toast.success("Configuration created");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.scanConfigs.update.useMutation({
    onSuccess: () => {
      utils.scanConfigs.list.invalidate();
      toast.success("Configuration updated");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    defaultValues: {
      name: "",
      city: "",
      sponsorName: "",
      includedTypes: [...DEFAULT_INCLUDED_TYPES].join("\n"),
      classificationTypes: [...DEFAULT_CLASSIFICATION_TYPES].join("\n"),
      excludeInstitutions: true,
      excludePrivate: true,
      excludeNoSportField: true,
      classificationModel: DEFAULT_SCAN_CONFIG_SETTINGS.classificationModel,
      websiteClassificationModel: DEFAULT_SCAN_CONFIG_SETTINGS.websiteClassificationModel,
      webResearchModel: DEFAULT_SCAN_CONFIG_SETTINGS.webResearchModel,
      maxDepth: String(DEFAULT_SCAN_CONFIG_SETTINGS.maxDepth),
      minCellArea: String(DEFAULT_SCAN_CONFIG_SETTINGS.minCellArea),
      concurrency: String(DEFAULT_SCAN_CONFIG_SETTINGS.concurrency),
      screenshotZoom: String(DEFAULT_SCAN_CONFIG_SETTINGS.screenshotZoom),
      rateLimit: String(DEFAULT_SCAN_CONFIG_SETTINGS.rateLimit),
    },
  });

  useEffect(() => {
    if (existingConfig) {
      const settings = existingConfig.settings as unknown as ScanConfigSettings;
      reset({
        name: existingConfig.name,
        city: existingConfig.city,
        sponsorName: existingConfig.sponsor_name ?? "",
        includedTypes: existingConfig.included_types.join("\n"),
        classificationTypes: existingConfig.classification_types.join("\n"),
        excludeInstitutions: existingConfig.exclude_institutions ?? true,
        excludePrivate: existingConfig.exclude_private ?? true,
        excludeNoSportField: existingConfig.exclude_no_sport_field ?? true,
        classificationModel: settings.classificationModel ?? DEFAULT_SCAN_CONFIG_SETTINGS.classificationModel,
        websiteClassificationModel: settings.websiteClassificationModel ?? DEFAULT_SCAN_CONFIG_SETTINGS.websiteClassificationModel,
        webResearchModel: settings.webResearchModel ?? DEFAULT_SCAN_CONFIG_SETTINGS.webResearchModel,
        maxDepth: String(settings.maxDepth ?? DEFAULT_SCAN_CONFIG_SETTINGS.maxDepth),
        minCellArea: String(settings.minCellArea ?? DEFAULT_SCAN_CONFIG_SETTINGS.minCellArea),
        concurrency: String(settings.concurrency ?? DEFAULT_SCAN_CONFIG_SETTINGS.concurrency),
        screenshotZoom: String(settings.screenshotZoom ?? DEFAULT_SCAN_CONFIG_SETTINGS.screenshotZoom),
        rateLimit: String(settings.rateLimit ?? DEFAULT_SCAN_CONFIG_SETTINGS.rateLimit),
      });
    }
  }, [existingConfig, reset]);

  const loadPreset = (presetKey: string) => {
    const preset = SCAN_CONFIG_PRESETS[presetKey];
    if (!preset) return;
    setValue("includedTypes", [...preset.includedTypes].join("\n"));
    setValue("classificationTypes", [...preset.classificationTypes].join("\n"));
    if (preset.settings.maxDepth) setValue("maxDepth", String(preset.settings.maxDepth));
    if (preset.settings.minCellArea) setValue("minCellArea", String(preset.settings.minCellArea));
    if (preset.settings.screenshotZoom) setValue("screenshotZoom", String(preset.settings.screenshotZoom));
    toast.success(`Loaded "${preset.name}" preset`);
  };

  // Tag input state derived from form values
  const includedTypesStr = watch("includedTypes");
  const classificationTypesStr = watch("classificationTypes");
  const includedTags = includedTypesStr.split("\n").map((s) => s.trim()).filter(Boolean);
  const classificationTags = classificationTypesStr.split("\n").map((s) => s.trim()).filter(Boolean);

  const onSubmit = (values: FormValues) => {
    const parseLines = (text: string) =>
      text.split("\n").map((s) => s.trim()).filter(Boolean);

    const settings: ScanConfigSettings = {
      classificationModel: values.classificationModel as ScanConfigSettings["classificationModel"],
      websiteClassificationModel: values.websiteClassificationModel as ScanConfigSettings["websiteClassificationModel"],
      webResearchModel: values.webResearchModel as ScanConfigSettings["webResearchModel"],
      maxDepth: parseInt(values.maxDepth),
      minCellArea: parseInt(values.minCellArea),
      concurrency: parseInt(values.concurrency),
      screenshotZoom: parseInt(values.screenshotZoom),
      rateLimit: parseInt(values.rateLimit),
      languageCode: "en",
      regionCode: "",
    };

    const payload = {
      name: values.name,
      city: values.city,
      includedTypes: parseLines(values.includedTypes),
      classificationTypes: parseLines(values.classificationTypes),
      settings,
      excludeInstitutions: values.excludeInstitutions,
      excludePrivate: values.excludePrivate,
      excludeNoSportField: values.excludeNoSportField,
      sponsorName: values.sponsorName || undefined,
    };

    if (configId) {
      updateMutation.mutate({ id: configId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (configId && isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center gap-4">
        <Button type="button" variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">
          {configId ? "Edit Configuration" : "New Configuration"}
        </h1>
      </div>

      {/* Preset Loader */}
      {!configId && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Load Preset</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2">
            {Object.entries(SCAN_CONFIG_PRESETS).map(([key, preset]) => (
              <Button
                key={key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => loadPreset(key)}
              >
                {preset.name}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Configuration Name</Label>
              <Input
                id="name"
                placeholder="e.g., Budapest Sports Scan"
                {...register("name", { required: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="e.g., Budapest"
                {...register("city", { required: true })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sponsorName">Sponsor Name</Label>
            <Input
              id="sponsorName"
              placeholder="e.g., Nike, Adidas (optional)"
              {...register("sponsorName")}
            />
            <p className="text-xs text-muted-foreground">
              Optional. Used to tag locations associated with a sponsor.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Place Types */}
      <Card>
        <CardHeader>
          <CardTitle>Included Place Types</CardTitle>
        </CardHeader>
        <CardContent>
          <TagInput
            tags={includedTags}
            onChange={(newTags) => setValue("includedTypes", newTags.join("\n"))}
            placeholder="Type a Google Places type and press Enter"
            showBulkEdit
          />
          <p className="text-xs text-muted-foreground mt-2">
            Google Places API types to include in search results. Press Enter to add.
          </p>
        </CardContent>
      </Card>

      {/* Classification Types */}
      <Card>
        <CardHeader>
          <CardTitle>Classification Types</CardTitle>
        </CardHeader>
        <CardContent>
          <TagInput
            tags={classificationTags}
            onChange={(newTags) => setValue("classificationTypes", newTags.join("\n"))}
            placeholder="Type a sport/facility type and press Enter"
            showBulkEdit
          />
          <p className="text-xs text-muted-foreground mt-2">
            Sport/facility types the AI classifier will identify. Press Enter to add.
          </p>
        </CardContent>
      </Card>

      {/* Exclusion Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Exclusion Filters</CardTitle>
          <CardDescription>
            Automatically reject locations that match these criteria during scanning.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="excludeInstitutions"
              checked={watch("excludeInstitutions")}
              onCheckedChange={(checked) => setValue("excludeInstitutions", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="excludeInstitutions" className="cursor-pointer">
                Exclude institutional locations
              </Label>
              <p className="text-xs text-muted-foreground">
                Schools, universities, colleges, and preschools
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="excludePrivate"
              checked={watch("excludePrivate")}
              onCheckedChange={(checked) => setValue("excludePrivate", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="excludePrivate" className="cursor-pointer">
                Exclude private facilities
              </Label>
              <p className="text-xs text-muted-foreground">
                Private gyms, clubs, and membership-only venues
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="excludeNoSportField"
              checked={watch("excludeNoSportField")}
              onCheckedChange={(checked) => setValue("excludeNoSportField", checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="excludeNoSportField" className="cursor-pointer">
                Exclude locations without sport fields
              </Label>
              <p className="text-xs text-muted-foreground">
                Locations where satellite imagery shows no sport field
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI & Advanced Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Satellite Classification Model</Label>
              <Select
                value={watch("classificationModel")}
                onValueChange={(v) => setValue("classificationModel", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Website Classification Model</Label>
              <Select
                value={watch("websiteClassificationModel")}
                onValueChange={(v) => setValue("websiteClassificationModel", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Web Research Model</Label>
              <Select
                value={watch("webResearchModel")}
                onValueChange={(v) => setValue("webResearchModel", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Max Depth</Label>
              <Input type="number" min={1} max={12} {...register("maxDepth")} />
            </div>
            <div className="space-y-2">
              <Label>Min Cell Area (m²)</Label>
              <Input type="number" min={100} {...register("minCellArea")} />
            </div>
            <div className="space-y-2">
              <Label>Concurrency</Label>
              <Input type="number" min={1} max={50} {...register("concurrency")} />
            </div>
            <div className="space-y-2">
              <Label>Screenshot Zoom</Label>
              <Input type="number" min={15} max={21} {...register("screenshotZoom")} />
            </div>
            <div className="space-y-2">
              <Label>Rate Limit (req/min)</Label>
              <Input type="number" min={1} {...register("rateLimit")} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {configId ? "Update" : "Create"} Configuration
        </Button>
      </div>
    </form>
  );
}
